import React, { useEffect, useState } from 'react';
import Breadcrumbs from 'components/atoms/Breadcrumbs';
import { ERouteLinks } from 'models/route';
import { StyledPageTitle } from 'theme/styles';
import { useTranslations } from 'hooks/useTranslations';
import Grid from '@mui/material/Grid';
import { Box } from '@mui/system';
import { FormInput } from 'components/molecules/FormInput';
import {
  StyledFilterChipWrapper,
  StyledSelectedFiltersWrapper,
} from 'pages/MyFiltersPage/styles';
import { Typography } from '@mui/material';
import { FilterChip } from 'components/atoms/FilterChip';
import { FormCheckbox } from 'components/molecules/FormCheckbox';
import Button from 'components/atoms/Button';
import { EButtonVariants } from 'constants/Buttons';
import { Alert, EAlertVariants } from 'components/atoms/Alert';
import FormWrapper from 'components/atoms/FormWrapper';
import { Resolver, useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import { useLocation, useNavigate } from 'react-router-dom';
import { mapValuesToLicencePresetRequest } from 'pages/LicencesPage/SaveLicencePresetPage/helpers';
import SearchFilterStore from 'state/SearchFilterStore';
import { ESearchFilterKeys } from 'constants/SearchFilterTypes';
import { useSavePreset } from 'pages/LicencesPage/SaveLicencePresetPage/hooks';
import { useAlert } from 'models/alertContext';
import { objectToKeyValueArray } from 'helpers/objects';
import { ActionTypes } from 'state/actions/alert';
import usePresetFormSchema from 'pages/LicencesPage/SaveLicencePresetPage/schema';
import { prepareFilters } from 'helpers/filters';
import { useLicenceContext } from 'models/licenceContext';
import { EDictionaryTypes } from 'models/dictionary';
import { useLang } from 'models/langContext';
import { useLicences } from '../hooks';
import { getStatusDataOptions } from '../helpers';
import { mapValuesWithDictionariesHelper } from 'helpers/mapValuesWithDictionariesHelper';
import { useGlobalProperty } from 'models/globalPropertyContext';

export enum ESearchForLicenceLabels {
  weaponLicenceNumber = 'licences.weaponLicenceNumber.label',
  weaponLicenceName = 'licences.licenceName.label',
  legalName = 'licences.legalName.label',
  legalTypeUniqueId = 'licences.legalType.label',
  statusesUniqueIds = 'licences.status.label',
  expiryDateFrom = 'licences.expiryDateFrom.label',
  expiryDateTo = 'licences.expiryDateTo.label',
  licenceAuthorityUniqueIds = 'licences.licencingAuthority.label',
}

export interface IPresetFormValues {
  name: string;
  emailNotificationEnabled: boolean;
  notificationEnabled: boolean;
}

const initialFormValues = {
  name: '',
  emailNotificationEnabled: false,
  notificationEnabled: false,
};

const SaveLicencePresetPage = () => {
  const { t } = useTranslations();
  const { schema } = usePresetFormSchema();
  const navigate = useNavigate();
  const { savePreset } = useSavePreset();
  const { setAlert } = useAlert();
  const [error, setError] = useState<string>('');
  const [isSubmitLoading, setIsSubmitLoading] = useState<boolean>(false);
  const { isOnlineLicence } = useLicenceContext();
  const { shortDateFormat } = useGlobalProperty();
  const { state: routerState }: any = useLocation();

  const {
    control,
    handleSubmit,
    formState: { errors },
    setValue,
  } = useForm<IPresetFormValues>({
    defaultValues: initialFormValues,
    resolver: yupResolver(schema) as unknown as Resolver<IPresetFormValues>,
  });
  const [filtersArray, setFiltersArray] = useState<{ key: string; value: string }[]>([]);
  const { selectedLanguage } = useLang();
  const { loadDictionaries, legalType, weaponLicencingAuthority } = useLicences();

  const getBackRoute = () => navigate(routerState.cancelPath || ERouteLinks.LicencesPage);
  const labels = ESearchForLicenceLabels;
  const filters = SearchFilterStore.getValue(ESearchFilterKeys.licences);

  useEffect(() => {
    (async function init() {
      await loadDictionaries([
        EDictionaryTypes.legalType,
        EDictionaryTypes.weaponLicencingAuthority,
      ]);
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedLanguage]);

  const filtersWithDictionaries = () => {
    setFiltersArray(
      mapValuesWithDictionariesHelper(objectToKeyValueArray(filters), {
        statusesUniqueIds: prepareFilters(getStatusDataOptions(t), 'value', 'label'),
        legalTypeUniqueId: prepareFilters(legalType, 'uniqueId', 'name'),
        licenceAuthorityUniqueIds: prepareFilters(
          weaponLicencingAuthority,
          'uniqueId',
          'name',
        ),
      }),
    );
  };

  useEffect(() => {
    if (filters) {
      filtersWithDictionaries();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [legalType]);

  const onSubmitForm = async (data: IPresetFormValues) => {
    setError('');
    setIsSubmitLoading(true);

    const body = mapValuesToLicencePresetRequest(filters, shortDateFormat);

    if (body) {
      try {
        const res = await savePreset({
          name: data.name,
          emailNotificationEnabled: data.emailNotificationEnabled,
          notificationEnabled: data.notificationEnabled,
          body,
        });

        if (res.status === 200) {
          setAlert(ActionTypes.SET_SAME_PAGE_ALERT, {
            text: `${t('saveMyFilter.successConfirmation.info')}`,
            variant: EAlertVariants.success,
          });
          return navigate(ERouteLinks.MyFilters);
        }
        setError(res.response.data.message);
      } catch (e: any) {
        console.error(e);
      }
    }
    return setIsSubmitLoading(false);
  };

  return (
    <>
      <Breadcrumbs
        items={[
          {
            label: t('module.licences.name'),
            route: ERouteLinks.LicencesPage,
          },
          t('licencesList.saveAsMyFilter.link'),
        ]}
      />
      <StyledPageTitle variant="h4">{t('licencesList.saveAsMyFilter.link')}</StyledPageTitle>

      <FormWrapper>
        <form onSubmit={handleSubmit(onSubmitForm)}>
          <Grid container>
            <Grid size={{ xs: 12, lg: 6 }}>
              <Box sx={{ mb: 1 }}>
                <FormInput
                  name="name"
                  label={`${t('saveMyFilter.filterName.label')}*`}
                  placeholder={t('saveMyFilter.filterName.label')}
                  control={control}
                  setValue={setValue}
                  errors={errors}
                />
              </Box>
            </Grid>

            <Grid size={{ xs: 12 }}>
              <StyledSelectedFiltersWrapper container style={{ rowGap: 1 }}>
                <Grid>
                  <Typography variant="body2">{`${t(
                    'saveMyFilter.selectedCriteria.section',
                  )}:`}</Typography>
                </Grid>
                <StyledFilterChipWrapper>
                  {filtersArray &&
                    filtersArray.map((item: { key: string; value: string }) => (
                      <FilterChip
                        key={item.key}
                        name={item.key}
                        label={t(labels?.[item.key as keyof typeof labels]) || item.key}
                        values={item.value}
                      />
                    ))}
                </StyledFilterChipWrapper>
              </StyledSelectedFiltersWrapper>
            </Grid>

            <Grid size={{ xs: 12 }} container columnSpacing={3}>
              <Grid size={{ xs: 12 }}>
                <FormCheckbox
                  name="notificationEnabled"
                  label={t('saveMyFilter.notifyMeViaSystem.checkbox.label')}
                  control={control}
                  errors={errors}
                />
              </Grid>

              {isOnlineLicence() && (
                <Grid size={{ xs: 12 }}>
                  <FormCheckbox
                    name="emailNotificationEnabled"
                    label={t('saveMyFilter.notifyMeViaEmail.checkbox.label')}
                    control={control}
                    errors={errors}
                  />
                </Grid>
              )}
            </Grid>

            <Grid container columnSpacing={3} size={{ xs: 12 }} sx={{ rowGap: 3, mt: 2 }}>
              <Grid size={{ xs: 12, lg: 3 }}>
                <Button
                  type="submit"
                  fullWidth
                  label={t('general.save.button')}
                  variant={EButtonVariants.contained}
                  isLoading={isSubmitLoading}
                />
              </Grid>

              <Grid size={{ xs: 12, lg: 3 }}>
                <Button
                  fullWidth
                  label={t('general.cancel.button')}
                  variant={EButtonVariants.outlined}
                  onClick={getBackRoute}
                />
              </Grid>
            </Grid>
            <Grid size={{ xs: 12 }} sx={{ mt: 2 }}>
              <Typography variant="caption">{t('general.mandatoryField.text')}</Typography>
            </Grid>
            {error && (
              <Grid size={{ xs: 12 }} sx={{ mt: 2 }}>
                <Alert text={error} variant={EAlertVariants.error} />
              </Grid>
            )}
          </Grid>
        </form>
      </FormWrapper>
    </>
  );
};

export { SaveLicencePresetPage };
